import React, { useEffect, useState } from "react";
import {
  FaFilter,
  FaSearch,
  FaChevronRight,
  FaChevronLeft,
} from "react-icons/fa";
import { components } from "react-select";
import { Link } from "react-router-dom";
import {
  Accordion,
  AccordionItem,
  AccordionItemHeading,
  AccordionItemButton,
  AccordionItemPanel,
} from "react-accessible-accordion";
import Switch from "react-switch";
import PickDate from "../../components/common/datePicker";
import { getCareSummaries } from "../../services/plan_service";
import Footer from "../../components/footer";
import GetCarePlan from "../../hooks/getCarePlan";
import moment from "moment";
import { formatInEt } from "../../utils/helper";
import Skeleton, { SkeletonTheme } from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import usePreventBrowserBack from "../../hooks/usePreventBrowserBack";

const Option = (props) => {
  return (
    <div>
      <components.Option {...props}>
        <input
          type="checkbox"
          checked={props.isSelected}
          onChange={() => null}
        />{" "}
        <label>{props.label}</label>
      </components.Option>
    </div>
  );
};

const CarePlanSummaries = (props) => {
  usePreventBrowserBack(Boolean(props.location?.state?.preventBrowserBack));
  // Filter states
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [filterStartDate, setFilterStartDate] = useState(null);
  const [filterEndDate, setFilterEndDate] = useState(null);
  const [selectedCarePlanTypes, setSelectedCarePlanTypes] = useState([]);
  const [filterCarePlanTypes, setFilterCarePlanTypes] = useState(null);
  const [selectedMeetingTypes, setSelectedMeetingTypes] = useState([]);
  const [filterMeetingTypes, setFilterMeetingTypes] = useState(null);
  const [selectedAuthors, setSelectedAuthors] = useState([]);
  const [filterAuthors, setFilterAuthors] = useState(null);
  const [availableCarePlanTypes, setAvailableCarePlanTypes] = useState([]);
  const [availableMeetingTypes, setAvailableMeetingTypes] = useState([]);
  const [availableAuthors, setAvailableAuthors] = useState([]);

  // Search state
  const [searchTerm, setSearchTerm] = useState("");

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Switch states
  const [checkedCarePlanTypes, setCheckedCarePlanTypes] = useState({
    check: false,
    attr: "",
  });
  const [checkedMeetingTypes, setCheckedMeetingTypes] = useState({
    check: false,
    attr: "",
  });
  const [checkedAuthors, setCheckedAuthors] = useState({
    check: false,
    attr: "",
  });

  const { data, isLoading, error } = GetCarePlan(
    filterStartDate,
    filterEndDate,
    filterCarePlanTypes,
    filterMeetingTypes,
    filterAuthors,
    currentPage,
    pageSize
  );
  const [latestDate, setLatestDate] = useState(null);
  const [oldestDate, setOldestDate] = useState(null);
  useEffect(() => {
    moment.updateLocale("en", {
      monthsShort: [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sept",
        "Oct",
        "Nov",
        "Dec",
      ],
    });
  }, []);
  useEffect(() => {
    if (data?.data && data?.data?.content && data.data.content.length > 0) {
      const momentDates = data.data.content.map((item) =>
        moment(item.entryDate)
      );
      const carePlanTypesSet = new Set();
      const meetingTypesSet = new Set();
      const authorsMap = new Map();

      data.data.content.forEach((item) => {
        // Extract care plan types
        if (item.type) {
          carePlanTypesSet.add(item.type);
        }

        // Extract meeting types
        if (item.typeOfMeeting) {
          meetingTypesSet.add(item.typeOfMeeting);
        }

        // Extract authors
        if (item.author && item.author.id) {
          const authorKey = `${item.author?.lastName}, ${item.author?.firstName}`;
          const positionAbbr = item.author.position?.abbreviation || "N/A";

          if (!authorsMap.has(item.author.id)) {
            authorsMap.set(item.author.id, {
              id: item.author.id,
              name: authorKey,
              position: positionAbbr,
              fullName: `${authorKey}: ${positionAbbr}`,
            });
          }
        }
      });

      const latest = moment.max(momentDates);
      setLatestDate(latest);

      const oldest = moment.min(momentDates);
      setOldestDate(oldest);

      // Set available filter options
      setAvailableCarePlanTypes(Array.from(carePlanTypesSet));
      setAvailableMeetingTypes(Array.from(meetingTypesSet));
      setAvailableAuthors(Array.from(authorsMap.values()));
    }
  }, [data]);

  // Date filter functions
  const handleTodayFilter = () => {
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    setStartDate(today);
    setEndDate(tomorrow);
  };

  const handleLast30DaysFilter = () => {
    const today = new Date();
    const thirtyDaysAgo = new Date(today);
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

    setStartDate(thirtyDaysAgo);
    setEndDate(today);
  };

  const handleLast7DaysFilter = () => {
    const today = new Date();
    const sevenDaysAgo = new Date(today);
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

    setStartDate(sevenDaysAgo);
    setEndDate(today);
  };

  // Format date for API (YYYY-MM-DDTHH:mm:ss.SSS)
  const formatDateForAPI = (date) => {
    if (!date) return null;
    return moment(date).format("YYYY-MM-DDTHH:mm:ss.SSS");
  };

  // Handle switch changes
  const handleChecked = (status, e, id) => {
    switch (id) {
      case "carePlanTypes":
        setCheckedCarePlanTypes({ check: status, attr: id });
        if (!status) {
          setSelectedCarePlanTypes([]);
        }
        break;
      case "meetingTypes":
        setCheckedMeetingTypes({ check: status, attr: id });
        if (!status) {
          setSelectedMeetingTypes([]);
        }
        break;
      case "authors":
        setCheckedAuthors({ check: status, attr: id });
        if (!status) {
          setSelectedAuthors([]);
        }
        break;
      default:
        break;
    }
  };

  // Handle care plan type selection
  const handleCarePlanTypeSelection = (type, isChecked) => {
    if (isChecked) {
      setSelectedCarePlanTypes([...selectedCarePlanTypes, type]);
    } else {
      setSelectedCarePlanTypes(selectedCarePlanTypes.filter((t) => t !== type));
    }
  };

  // Handle meeting type selection
  const handleMeetingTypeSelection = (type, isChecked) => {
    if (isChecked) {
      setSelectedMeetingTypes([...selectedMeetingTypes, type]);
    } else {
      setSelectedMeetingTypes(selectedMeetingTypes.filter((t) => t !== type));
    }
  };

  // Handle author selection
  const handleAuthorSelection = (authorId, isChecked) => {
    if (isChecked) {
      setSelectedAuthors([...selectedAuthors, authorId]);
    } else {
      setSelectedAuthors(selectedAuthors.filter((id) => id !== authorId));
    }
  };

  // Handle search button click
  const handleSearch = () => {
    const formattedStartDate = formatDateForAPI(startDate);
    const formattedEndDate = formatDateForAPI(endDate);

    setFilterStartDate(formattedStartDate);
    setFilterEndDate(formattedEndDate);

    // Set care plan type filter based on switch state and selected types
    if (checkedCarePlanTypes.check && selectedCarePlanTypes.length > 0) {
      setFilterCarePlanTypes(selectedCarePlanTypes);
    } else {
      setFilterCarePlanTypes(null);
    }

    // Set meeting type filter based on switch state and selected types
    if (checkedMeetingTypes.check && selectedMeetingTypes.length > 0) {
      setFilterMeetingTypes(selectedMeetingTypes);
    } else {
      setFilterMeetingTypes(null);
    }

    // Set author filter based on switch state and selected authors
    if (checkedAuthors.check && selectedAuthors.length > 0) {
      setFilterAuthors(selectedAuthors);
    } else {
      setFilterAuthors(null);
    }

    // Close the modal
    window.$("#filterModal").modal("hide");
  };

  // Clear filters
  const handleClearFilters = () => {
    setStartDate(null);
    setEndDate(null);
    setFilterStartDate(null);
    setFilterEndDate(null);
    setSelectedCarePlanTypes([]);
    setFilterCarePlanTypes(null);
    setSelectedMeetingTypes([]);
    setFilterMeetingTypes(null);
    setSelectedAuthors([]);
    setFilterAuthors(null);
    setCheckedCarePlanTypes({ check: false, attr: "" });
    setCheckedMeetingTypes({ check: false, attr: "" });
    setCheckedAuthors({ check: false, attr: "" });
    setSearchTerm("");
  };

  // Entry date: the meeting/entry date the author picked, shown as a plain date
  // to match the title on the single care plan view.
  const formatDate = (date) => {
    return formatInEt(date, "D MMM YYYY");
  };

  // Document creation date: when the document (this version) was actually
  // created — the same value the single care plan view shows against
  // "Created by:" for the original version.
  const formatTimestamp = (isoString) =>
    isoString ? formatInEt(isoString, "D MMM YYYY, h:mm A") : "—";

  // Filter data based on search term
  const getFilteredData = () => {
    const baseData = data?.data?.content || [];

    if (!searchTerm.trim()) {
      return baseData;
    }

    const searchLower = searchTerm.toLowerCase().trim();

    const filtered = baseData.filter((item) => {
      // Search in entry date and document creation date, as displayed
      const dateMatch =
        formatDate(item.entryDate).toLowerCase().includes(searchLower) ||
        formatTimestamp(item.dateCreated).toLowerCase().includes(searchLower);

      // Search in type of care plan
      const carePlanTypeMatch = item.type?.toLowerCase().includes(searchLower);

      // Search in type of meeting
      const meetingTypeMatch = item.typeOfMeeting
        ?.toLowerCase()
        .includes(searchLower);

      // Search in author (firstName + lastName)
      const authorFullName = `${item.author?.firstName || ""} ${
        item.author?.lastName || ""
      }`.toLowerCase();
      const authorLastFirst = `${item.author?.lastName || ""}, ${
        item.author?.firstName || ""
      }`.toLowerCase();
      const authorMatch =
        authorFullName.includes(searchLower) ||
        authorLastFirst.includes(searchLower);

      // Search in position
      const positionMatch =
        item.author?.position?.abbreviation
          ?.toLowerCase()
          .includes(searchLower) ||
        item.author?.position?.name?.toLowerCase().includes(searchLower);

      return (
        dateMatch ||
        carePlanTypeMatch ||
        meetingTypeMatch ||
        authorMatch ||
        positionMatch
      );
    });

    // Apply client-side pagination to search results
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return filtered.slice(startIndex, endIndex);
  };

  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1); // Reset to first page when search changes
  };

  // Clear search
  const clearSearch = () => {
    setSearchTerm("");
  };

  // Pagination handlers
  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setCurrentPage(1); // Reset to first page when changing page size
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    const totalPages = Math.ceil((data?.data?.totalElements || 0) / pageSize);
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  // Calculate pagination info
  const isSearchActive = searchTerm.trim().length > 0;

  // Get total count of filtered results (before pagination)
  const getTotalFilteredCount = () => {
    if (!isSearchActive) {
      return data?.data?.totalElements || 0;
    }

    const baseData = data?.data?.content || [];
    const searchLower = searchTerm.toLowerCase().trim();

    return baseData.filter((item) => {
      // Same search logic as getFilteredData — keep the two in sync or the
      // pager count stops matching the rows on screen.
      const dateMatch =
        formatDate(item.entryDate).toLowerCase().includes(searchLower) ||
        formatTimestamp(item.dateCreated).toLowerCase().includes(searchLower);
      const carePlanTypeMatch = item.type?.toLowerCase().includes(searchLower);
      const meetingTypeMatch = item.typeOfMeeting
        ?.toLowerCase()
        .includes(searchLower);
      const authorFullName = `${item.author?.firstName || ""} ${
        item.author?.lastName || ""
      }`.toLowerCase();
      const authorLastFirst = `${item.author?.lastName || ""}, ${
        item.author?.firstName || ""
      }`.toLowerCase();
      const authorMatch =
        authorFullName.includes(searchLower) ||
        authorLastFirst.includes(searchLower);
      const positionMatch =
        item.author?.position?.abbreviation
          ?.toLowerCase()
          .includes(searchLower) ||
        item.author?.position?.name?.toLowerCase().includes(searchLower);

      return (
        dateMatch ||
        carePlanTypeMatch ||
        meetingTypeMatch ||
        authorMatch ||
        positionMatch
      );
    }).length;
  };

  const totalElements = getTotalFilteredCount();
  const totalPages = Math.ceil(totalElements / pageSize);
  const hasNextPage = currentPage < totalPages;
  const hasPreviousPage = currentPage > 1;

  return (
    <>
      <div className="container-fluid pt-12 pl-21 pb-12 pr-24 h-100">
        <div className="card border-0 h-100">
          <div className="card-body">
            <h5 className="font-weight-lighter">
              HCP: VIEW Care Plan Summaries
            </h5>
            <hr />
            <div className="d-flex justify-content-between align-items-center flex-wrap mt-4">
              <h6 className="font-weight-lighter mt-2">
                <small>
                  Time period: &nbsp;&nbsp;&nbsp;{" "}
                  {isLoading ? (
                    <Skeleton height={16} width={200} />
                  ) : (
                    <>
                      {oldestDate && formatDate(oldestDate)} to{" "}
                      {latestDate && formatDate(latestDate)}
                    </>
                  )}
                  {searchTerm.trim() && (
                    <>
                      <br />
                      <span className="text_primary">
                        Search results: {getFilteredData().length} of{" "}
                        {data?.data?.content?.length || 0} care plans
                      </span>
                    </>
                  )}
                </small>
              </h6>
              <div className="d-flex align-items-center" style={{ width: 355 }}>
                <div className="input-group bg_muted rounded">
                  <div className="input-group-prepend">
                    <span
                      className="input-group-text border-0 bg-transparent text-muted pr-3"
                      id="basic-addon1"
                    >
                      <FaSearch size={16} color="#c2c2c2" />
                    </span>
                  </div>
                  <input
                    type="text"
                    className="form-control border-none bg_muted f-14 text-black outline-none rounded mr-3"
                    placeholder="Search"
                    aria-label="Search"
                    aria-describedby="basic-addon1"
                    value={searchTerm}
                    onChange={handleSearchChange}
                  />
                </div>
                <button
                  className="btn btn-outline-primary w-50 ml-3 rounded py-1"
                  data-toggle="modal"
                  data-target="#filterModal"
                >
                  <FaFilter size={16} />
                  &nbsp;&nbsp; <small>Filter</small>
                </button>
              </div>
            </div>
            <table className="table table-striped table-bordered mt-4 text-center">
              <thead className="head_light text-white">
                <tr>
                  <th scope="col">Document creation date</th>
                  <th scope="col">Entry Date</th>
                  <th scope="col">Type of Care Plan</th>
                  <th scope="col">Type of Meeting</th>
                  <th scope="col">Author</th>
                  <th scope="col">Position</th>
                  <th scope="col">Go to Care Plan</th>
                </tr>
              </thead>
              <tbody>
                {(() => {
                  const filteredData = getFilteredData();
                  return !isLoading && filteredData.length > 0 ? (
                    filteredData.map((item, i) => (
                      <tr key={i}>
                        <th>
                          <small>{formatTimestamp(item?.dateCreated)}</small>
                          {/* Version badge — only for amended versions (v2+),
                              never the original */}
                          {item?.versionNumber > 1 && (
                            <span
                              className="ml-2 d-inline-flex align-items-center justify-content-center"
                              title={`Version ${item.versionNumber}`}
                              style={{
                                width: 18,
                                height: 18,
                                borderRadius: "50%",
                                border: "1px solid #adb5bd",
                                color: "#6c757d",
                                fontSize: 9,
                                fontWeight: 600,
                                lineHeight: 1,
                              }}
                            >
                              v{item.versionNumber}
                            </span>
                          )}
                          {/* Link to the prior version, when one exists */}
                          {/* {item?.versionNumber > 1 && item?.previousVersionId && (
                            <div>
                              <Link
                                to={{
                                  pathname: `/hcp/patient/weekly_conference`,
                                  state: { carePlanId: item.previousVersionId, carePlanType: item.type },
                                }}
                                className="f-11"
                                style={{ fontSize: 11 }}
                              >
                                Link to previous version
                              </Link>
                            </div>
                          )} */}
                        </th>
                        <td>
                          <small>{formatDate(item?.entryDate)}</small>
                        </td>
                        <td>
                          <small>{item?.type === "INDIVIDUAL" ? "Individual" : item?.type === "COLLABORATIVE" ? "Collaborative" : item?.type}</small>
                        </td>
                        <td>
                          <small>
                            {item.typeOfMeeting ? item.typeOfMeeting : ""}
                          </small>
                        </td>
                        <td>
                          <small>
                            {item.author?.lastName}, {item.author?.firstName}
                          </small>
                        </td>
                        <td>
                          <small>{item.author.position.abbreviation}</small>
                        </td>
                        <td>
                          <Link
                            to={{
                              pathname: `/hcp/patient/weekly_conference`,
                              state: { carePlanId: item.id, carePlanType: item.type },
                            }}
                            className="border_success rounded-pill px-4"
                          >
                            <i className="fa fa-chevron-right" />
                          </Link>
                        </td>
                      </tr>
                    ))
                  ) : !isLoading ? (
                    <tr>
                      <td colSpan="7">
                        <small className="text-center">
                          {searchTerm.trim()
                            ? `No care plan summaries found matching "${searchTerm}"`
                            : "No care plan summaries found"}
                        </small>
                      </td>
                    </tr>
                  ) : null;
                })()}
                {isLoading && (
                  <SkeletonTheme baseColor="#e9ecef" highlightColor="#f8f9fa">
                    {[...Array(8)].map((_, index) => (
                      <tr key={`skeleton-${index}`}>
                        <th>
                          <small>
                            <Skeleton width={120} height={12} />
                          </small>
                        </th>
                        <td>
                          <small>
                            <Skeleton width={90} height={12} />
                          </small>
                        </td>
                        <td>
                          <small>
                            <Skeleton width={120} height={12} />
                          </small>
                        </td>
                        <td>
                          <small>
                            <Skeleton width={100} height={12} />
                          </small>
                        </td>
                        <td>
                          <small>
                            <Skeleton width={100} height={12} />
                          </small>
                        </td>
                        <td>
                          <small>
                            <Skeleton width={80} height={12} />
                          </small>
                        </td>
                        <td>
                          <Skeleton width={20} height={12} />
                        </td>
                      </tr>
                    ))}
                  </SkeletonTheme>
                )}
              </tbody>
            </table>

            {/* Pagination Controls - Only show if there are documents */}
            {!isLoading &&
              data?.data &&
              data.data.content &&
              data.data.content.length > 0 && (
                <div className="d-flex justify-content-between align-items-center mt-4">
                  {/* Page Size Dropdown - Bottom Left */}
                  <div className="d-flex align-items-center">
                    <span
                      className="text-dark mr-2"
                      style={{ fontSize: "12px", fontWeight: "500" }}
                    >
                      Show
                    </span>
                    <select
                      className="border rounded"
                      style={{
                        width: "50px",
                        height: "24px",
                        fontSize: "12px",
                        fontWeight: "500",
                        color: "#495057",
                        backgroundColor: "#fff",
                        border: "1px solid #ced4da",
                        borderRadius: "6px",
                        padding: "2px 4px",
                        outline: "none",
                      }}
                      value={pageSize}
                      onChange={(e) =>
                        handlePageSizeChange(parseInt(e.target.value))
                      }
                    >
                      <option value={5}>5</option>
                      <option value={10}>10</option>
                      <option value={15}>15</option>
                      <option value={20}>20</option>
                      <option value={25}>25</option>
                    </select>
                    <span
                      className="text-dark ml-2"
                      style={{ fontSize: "12px", fontWeight: "500" }}
                    >
                      entries
                    </span>
                  </div>

                  {/* Pagination Navigation - Bottom Right */}
                  <div className="d-flex align-items-center">
                    <span
                      className="text-muted mr-4"
                      style={{ fontSize: "12px" }}
                    >
                      {totalElements > 0
                        ? `Showing ${Math.min(
                            (currentPage - 1) * pageSize + 1,
                            totalElements
                          )} to ${Math.min(
                            currentPage * pageSize,
                            totalElements
                          )} of ${totalElements} entries`
                        : "No entries to show"}
                    </span>

                    {totalPages > 1 && (
                      <nav aria-label="Page navigation">
                        <div className="d-flex align-items-center">
                          {/* Previous Button */}
                          <button
                            className="btn d-flex align-items-center justify-content-center mr-2"
                            style={{
                              width: "24px",
                              height: "24px",
                              border: "1px solid #ced4da",
                              borderRadius: "6px",
                              backgroundColor: !hasPreviousPage
                                ? "#d7d7d7"
                                : "#fff",
                              color: !hasPreviousPage
                                ? "rgba(83, 89, 107, 0.5)"
                                : "#495057",
                              cursor: !hasPreviousPage
                                ? "not-allowed"
                                : "pointer",
                              paddingLeft: "7px",
                              paddingRight: "7px",
                            }}
                            onClick={handlePreviousPage}
                            disabled={!hasPreviousPage}
                          >
                            <FaChevronLeft size={12} />
                          </button>

                          {/* Page Numbers */}
                          <div className="d-flex">
                            {Array.from(
                              { length: totalPages },
                              (_, i) => i + 1
                            ).map((pageNum) => (
                              <button
                                key={pageNum}
                                className="btn d-flex align-items-center justify-content-center mx-1"
                                style={{
                                  width: "24px",
                                  height: "24px",
                                  border:
                                    currentPage === pageNum
                                      ? "1px solid #53596b"
                                      : "1px solid #ced4da",
                                  borderRadius: "6px",
                                  backgroundColor:
                                    currentPage === pageNum
                                      ? "#53596b"
                                      : "#fff",
                                  color:
                                    currentPage === pageNum
                                      ? "#fff"
                                      : "#495057",
                                  fontSize: "12px",
                                  fontWeight: "500",
                                  cursor: "pointer",
                                }}
                                onClick={() => handlePageChange(pageNum)}
                              >
                                {pageNum}
                              </button>
                            ))}
                          </div>

                          {/* Next Button */}
                          <button
                            className="btn d-flex align-items-center justify-content-center ml-2"
                            style={{
                              width: "24px",
                              height: "24px",
                              border: "1px solid #ced4da",
                              borderRadius: "6px",
                              backgroundColor: !hasNextPage
                                ? "#d7d7d7"
                                : "#fff",
                              color: !hasNextPage
                                ? "rgba(83, 89, 107, 0.5)"
                                : "#495057",
                              cursor: !hasNextPage ? "not-allowed" : "pointer",
                              paddingLeft: "7px",
                              paddingRight: "7px",
                            }}
                            onClick={handleNextPage}
                            disabled={!hasNextPage}
                          >
                            <FaChevronRight size={12} />
                          </button>
                        </div>
                      </nav>
                    )}
                  </div>
                </div>
              )}
          </div>
        </div>
      </div>
      <Footer />

      {/* Filter Modal */}
      <div
        className="modal fade"
        id="filterModal"
        tabIndex="-1"
        role="dialog"
        aria-labelledby="filterModalLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog" role="document">
          <div className="modal-content pt-3">
            <div className="modal-header border-0">
              <div className="flex-grow-1 text-center">
                <h5 className="text_primary" style={{ fontWeight: 600 }}>
                  Search Filter
                </h5>
              </div>
              <button
                type="button"
                className="close"
                data-dismiss="modal"
                aria-label="Close"
              >
                <span
                  className="text_primary"
                  style={{ fontSize: 40, fontWeight: "600" }}
                  aria-hidden="true"
                >
                  &times;
                </span>
              </button>
            </div>
            <div className="modal-body mb-3 p-0">
              <div className="d-flex justify-content-between align-items-center mb-4 px-3">
                <button
                  className="btn bg_dark darkHover"
                  onClick={handleTodayFilter}
                >
                  <small>Today</small>
                </button>
                <button
                  className="btn bg_dark darkHover"
                  onClick={handleLast7DaysFilter}
                >
                  <small>Last 7 days</small>
                </button>
                <button
                  className="btn bg_dark darkHover"
                  onClick={handleLast30DaysFilter}
                >
                  <small>Last 30 days</small>
                </button>
              </div>
              <small className="text-muted ml-3">Custom Date</small>
              <hr className="my-2 mx-3" />
              <div className="row mb-3 mx-1">
                <div className="col-md-6">
                  <small className="text-muted">Start Date</small>
                  <PickDate
                    date={startDate}
                    setDate={setStartDate}
                    required={false}
                  />
                </div>
                <div className="col-md-6">
                  <small className="text-muted">End Date</small>
                  <PickDate
                    date={endDate}
                    setDate={setEndDate}
                    required={false}
                  />
                </div>
              </div>

              <Accordion allowZeroExpanded allowMultipleExpanded>
                <div className="chevron" />
                <AccordionItem uuid="care-plan-types-filter">
                  <div className="d-flex justify-content-between align-items-center pr-3">
                    <AccordionItemHeading className="accordion_transparent">
                      <AccordionItemButton>
                        <small className="text-muted">Type of Care Plan</small>
                      </AccordionItemButton>
                    </AccordionItemHeading>
                    <Switch
                      checked={
                        checkedCarePlanTypes.attr == "carePlanTypes" &&
                        checkedCarePlanTypes.check
                      }
                      onChange={handleChecked}
                      onColor="#C7E6F5"
                      onHandleColor="#50A193"
                      handleDiameter={25}
                      uncheckedIcon={false}
                      checkedIcon={false}
                      boxShadow="0px 1px 5px rgba(0, 0, 0, 0.6)"
                      height={16}
                      width={48}
                      className="react-switch"
                      id="carePlanTypes"
                    />
                  </div>
                  <AccordionItemPanel className="bg_lighter_primary m-0 py-2 px-4">
                    <small className="text-muted f-13">
                      Choose as many as applicable:
                    </small>
                    <div className="mt-2">
                      {availableCarePlanTypes.map((type, i) => (
                        <div
                          className="dropdown-item noFilterHover form-group form-check ml-4 wa mb-1"
                          key={i}
                        >
                          <input
                            type="checkbox"
                            className="form-check-input mt-2 mr-1"
                            id={`carePlanTypeCheck${i}`}
                            checked={selectedCarePlanTypes.includes(type)}
                            onChange={(e) =>
                              handleCarePlanTypeSelection(
                                type,
                                e.target.checked
                              )
                            }
                            disabled={!checkedCarePlanTypes.check}
                          />
                          <label
                            className="form-check-label f-13"
                            htmlFor={`carePlanTypeCheck${i}`}
                          >
                            <small>{type === "INDIVIDUAL" ? "Individual" : "Collaborative"}</small>
                          </label>
                        </div>
                      ))}
                      {availableCarePlanTypes.length === 0 && !isLoading && (
                        <div className="ml-4">
                          <small className="text-muted">
                            No care plan types found in current data
                          </small>
                        </div>
                      )}
                    </div>
                  </AccordionItemPanel>
                </AccordionItem>

                <AccordionItem uuid="meeting-types-filter">
                  <div className="d-flex justify-content-between align-items-center pr-3">
                    <AccordionItemHeading className="accordion_transparent">
                      <AccordionItemButton>
                        <small className="text-muted">Type of Meeting</small>
                      </AccordionItemButton>
                    </AccordionItemHeading>
                    <Switch
                      checked={
                        checkedMeetingTypes.attr == "meetingTypes" &&
                        checkedMeetingTypes.check
                      }
                      onChange={handleChecked}
                      onColor="#C7E6F5"
                      onHandleColor="#50A193"
                      handleDiameter={25}
                      uncheckedIcon={false}
                      checkedIcon={false}
                      boxShadow="0px 1px 5px rgba(0, 0, 0, 0.6)"
                      height={16}
                      width={48}
                      className="react-switch"
                      id="meetingTypes"
                    />
                  </div>
                  <AccordionItemPanel className="bg_lighter_primary m-0 py-2 px-4">
                    <small className="text-muted f-13">
                      Choose as many as applicable:
                    </small>
                    <div className="mt-2">
                      {availableMeetingTypes.map((type, i) => (
                        <div
                          className="dropdown-item noFilterHover form-group form-check ml-4 wa mb-1"
                          key={i}
                        >
                          <input
                            type="checkbox"
                            className="form-check-input mt-2 mr-1"
                            id={`meetingTypeCheck${i}`}
                            checked={selectedMeetingTypes.includes(type)}
                            onChange={(e) =>
                              handleMeetingTypeSelection(type, e.target.checked)
                            }
                            disabled={!checkedMeetingTypes.check}
                          />
                          <label
                            className="form-check-label f-13"
                            htmlFor={`meetingTypeCheck${i}`}
                          >
                            <small>{type === "INDIVIDUAL" ? "Individual" : type === "COLLABORATIVE" ? "Collaborative" : type}</small>
                          </label>
                        </div>
                      ))}
                      {availableMeetingTypes.length === 0 && (
                        <div className="ml-4">
                          <small className="text-muted">
                            No meeting types found in current data
                          </small>
                        </div>
                      )}
                    </div>
                  </AccordionItemPanel>
                </AccordionItem>

                <AccordionItem uuid="authors-filter">
                  <div className="d-flex justify-content-between align-items-center pr-3">
                    <AccordionItemHeading className="accordion_transparent">
                      <AccordionItemButton>
                        <small className="text-muted">Author</small>
                      </AccordionItemButton>
                    </AccordionItemHeading>
                    <Switch
                      checked={
                        checkedAuthors.attr == "authors" && checkedAuthors.check
                      }
                      onChange={handleChecked}
                      onColor="#C7E6F5"
                      onHandleColor="#50A193"
                      handleDiameter={25}
                      uncheckedIcon={false}
                      checkedIcon={false}
                      boxShadow="0px 1px 5px rgba(0, 0, 0, 0.6)"
                      height={16}
                      width={48}
                      className="react-switch"
                      id="authors"
                    />
                  </div>
                  <AccordionItemPanel className="bg_lighter_primary m-0 py-2 px-4">
                    <small className="text-muted f-13">
                      Choose as many as applicable:
                    </small>
                    <div className="mt-2">
                      {availableAuthors.map((author, i) => (
                        <div
                          className="dropdown-item noFilterHover form-group form-check ml-4 wa mb-1"
                          key={i}
                        >
                          <input
                            type="checkbox"
                            className="form-check-input mt-2 mr-1"
                            id={`authorCheck${author.id}`}
                            checked={selectedAuthors.includes(author.id)}
                            onChange={(e) =>
                              handleAuthorSelection(author.id, e.target.checked)
                            }
                            disabled={!checkedAuthors.check}
                          />
                          <label
                            className="form-check-label f-13"
                            htmlFor={`authorCheck${author.id}`}
                          >
                            <small>{author.fullName}</small>
                          </label>
                        </div>
                      ))}
                      {availableAuthors.length === 0 && (
                        <div className="ml-4">
                          <small className="text-muted">
                            No authors found in current data
                          </small>
                        </div>
                      )}
                    </div>
                  </AccordionItemPanel>
                </AccordionItem>
              </Accordion>
            </div>
            <div className="modal-footer d-flex justify-content-between align-items-center border-0">
              <button
                type="button"
                className="btn btn_outline_danger px-5"
                onClick={handleClearFilters}
                data-dismiss="modal"
              >
                CLEAR
              </button>
              <button
                type="button"
                className="btn btn-outline-primary px-5"
                onClick={handleSearch}
              >
                SEARCH
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CarePlanSummaries;
